import { computed, ref } from "vue";
import store from "@/store";

export interface InvoiceColumn {
  key: string;
  field: string;
  header: string;
  input: boolean;
  dictNames?: string[];
  correlativeIds?: string[];
  sortable?: boolean;
  bodyClass?: string;
  bodyStyle?: string;
  bodyType: "text" | "link" | "date" | "currency" | "type" | "status";
  /** Override field name used when reading row data for display (e.g. due_date for sortableDate). */
  dataField?: string;
  filterType: "none" | "text" | "calendar" | "dropdown";
  /** Component data-property key that holds the filter value. */
  filterStateKey?: string;
  /** Component data-property key that holds the dropdown options array. */
  filterOptionsKey?: string;
  filterPlaceholder?: string;
  filterApplyLabel?: string;
  /** Field name used for print/export when it differs from `field`. */
  printField?: string;
}

export const INVOICE_COLUMNS: InvoiceColumn[] = [
  {
    key: "ar_id", field: "ar_id", header: "Invoice", input: true,
    dictNames: ["AR.ID"],
    bodyType: "link", bodyStyle: "min-width: 100px",
    filterType: "text", filterStateKey: "invoiceNumber",
    filterPlaceholder: "Search by Id",
  },
  {
    key: "cust_name", field: "cust_name", header: "Customer Name", input: true,
    dictNames: ["CUST"], correlativeIds: ["CUST.NAME"],
    sortable: true, bodyType: "text", filterType: "none",
  },
  {
    key: "invoice_date", field: "invoice_date", header: "Invoice Date", input: true,
    dictNames: ["INVOICE.DATE"],
    sortable: true, bodyType: "date", filterType: "none",
  },
  {
    key: "sortableDate", field: "sortableDate", header: "Due Date", input: true,
    dictNames: ["DUE.DATE"],
    sortable: true, bodyType: "date", dataField: "due_date",
    filterType: "calendar", filterStateKey: "dueDateFilter",
    filterPlaceholder: "Select Date Range",
    printField: "due_date",
  },
  {
    key: "type", field: "type", header: "Type", input: true,
    dictNames: ["TYPE"],
    sortable: true, bodyType: "type",
    filterType: "dropdown", filterStateKey: "selectedType",
    filterOptionsKey: "types", filterPlaceholder: "Filter By Type",
    filterApplyLabel: "Search",
  },
  {
    key: "invoice_amt", field: "invoice_amt", header: "Invoice Amount", input: true,
    dictNames: ["INVOICE.AMT"],
    sortable: true, bodyType: "currency",
    bodyClass: "pr-2 xl:pr-6", bodyStyle: "text-align: right;",
    filterType: "none",
  },
  {
    key: "balance", field: "balance", header: "Balance", input: true,
    dictNames: ["BALANCE"],
    sortable: true, bodyType: "currency",
    bodyClass: "pr-2 xl:pr-6", bodyStyle: "text-align: right;",
    filterType: "none",
  },
  {
    key: "status", field: "status", header: "Status", input: true,
    dictNames: ["STATUS"],
    bodyType: "status",
    filterType: "dropdown", filterStateKey: "selectedStatus",
    filterOptionsKey: "stati", filterPlaceholder: "Filter By Status",
    filterApplyLabel: "Search",
  },
];

export function resolveInvoiceColumnVisibility(
  columns: InvoiceColumn[],
  visibilityResolver?: {
    fieldExists?: (dictName: string) => boolean;
    correlativeExists?: (correlativeId: string) => boolean;
  },
): Record<string, boolean> {
  const fieldExistsGetter = visibilityResolver?.fieldExists
    ?? ((dictName: string) => store.getters["fdict/fieldExists"]("AR", dictName));
  const correlativeExistsGetter = visibilityResolver?.correlativeExists
    ?? ((correlativeId: string) =>
      store.getters["fdict/correlativeExists"]("AR", correlativeId));

  return columns.reduce((visibilityMap: Record<string, boolean>, column) => {
    const hasDictField =
      Array.isArray(column.dictNames) &&
      column.dictNames.some((dictName) => fieldExistsGetter(dictName));
    const hasCorrelative =
      Array.isArray(column.correlativeIds) &&
      column.correlativeIds.some((correlativeId) =>
        correlativeExistsGetter(correlativeId),
      );

    visibilityMap[column.key] = Boolean(hasDictField || hasCorrelative);
    return visibilityMap;
  }, {});
}

export function getVisiblePrintableInvoiceColumns(
  columnVisibilityMap: Record<string, boolean>,
): { field: string; header: string; input: boolean }[] {
  return INVOICE_COLUMNS.filter(
    (column) => columnVisibilityMap[column.key] !== false,
  ).map((column) => ({
    field: column.printField || column.field,
    header: column.header,
    input: column.input,
  }));
}

export function useInvoiceColumnVisibility() {
  const columnVisibilityMap = ref<Record<string, boolean>>(
    INVOICE_COLUMNS.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {} as Record<string, boolean>),
  );

  const visibleColumns = computed(() =>
    INVOICE_COLUMNS.filter(
      (col) => columnVisibilityMap.value[col.key] !== false,
    ),
  );

  const visiblePrintableColumns = computed(() =>
    getVisiblePrintableInvoiceColumns(columnVisibilityMap.value),
  );

  function applyVisibility(
    visibilityResolver?: {
      fieldExists?: (dictName: string) => boolean;
      correlativeExists?: (correlativeId: string) => boolean;
    },
  ) {
    columnVisibilityMap.value = resolveInvoiceColumnVisibility(
      INVOICE_COLUMNS,
      visibilityResolver,
    );
  }

  function resetVisibility() {
    columnVisibilityMap.value = INVOICE_COLUMNS.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }

  return {
    INVOICE_COLUMNS,
    columnVisibilityMap,
    visibleColumns,
    visiblePrintableColumns,
    applyVisibility,
    resetVisibility,
  };
}
